// Azzurro vocabulary pillar — themed, CEFR-tiered, dual-coded (emoji) word cards.
//
// This is the NEW richer vocabulary dataset that backs the `vocabulary`
// PocketBase collection and the Vocabulary browse view. It is distinct from the
// small legacy `VOCABULARY` array in ./index.js (shape { es, en, cat }) that the
// old nouns/adjectives/flashcards views still read — that one is re-exported
// below for backward compatibility.
//
// Words and their meanings are language facts; the theme grouping, CEFR tiering,
// emoji pairing, and every example sentence are authored originally for Azzurro.
//
// Each row is a tuple kept terse on purpose so the data reads like a table:
//   [ italian, english, emoji, part_of_speech, gender, example_it, example_en ]
// gender is "" for anything that isn't a noun (the builder stores it as "none").

export { VOCABULARY } from "./index.js";

// Net-new, collision-free per-theme cards live in standalone vocab-<theme>.js
// modules and are folded in below via buildVocabulary().
import { EXTRA_THEME_BLOCKS } from "./vocab-expansion.js";

const THEMES = [
  // ---- Identity --------------------------------------------------------
  {
    theme: "identity", tier: "A1", rows: [
      ["il nome", "name", "🪪", "noun", "m", "Il mio nome è Anna.", "My name is Anna."],
      ["il cognome", "surname", "🔤", "noun", "m", "Qual è il tuo cognome?", "What is your surname?"],
      ["l'età", "age", "🎂", "noun", "f", "Ho trent'anni.", "I am thirty years old."],
      ["il paese", "country", "🌍", "noun", "m", "L'Italia è un bel paese.", "Italy is a beautiful country."],
      ["la nazionalità", "nationality", "🛂", "noun", "f", "La mia nazionalità è norvegese.", "My nationality is Norwegian."],
      ["l'indirizzo", "address", "🏡", "noun", "m", "Mi dai il tuo indirizzo?", "Will you give me your address?"],
      ["il telefono", "phone", "📞", "noun", "m", "Non ho il tuo numero di telefono.", "I don't have your phone number."],
      ["l'email", "email", "📧", "noun", "f", "Ti mando un'email.", "I'll send you an email."],
      ["single", "single", "🙋", "adjective", "", "Mio fratello è single.", "My brother is single."],
      ["sposato", "married", "💑", "adjective", "", "Sono sposati da maggio.", "They have been married since May."],
    ],
  },
  {
    theme: "identity", tier: "A2", rows: [
      ["il documento", "document", "📄", "noun", "m", "Devo firmare il documento.", "I need to sign the document."],
      ["la firma", "signature", "✍️", "noun", "f", "Manca la tua firma qui.", "Your signature is missing here."],
      ["il cittadino", "citizen", "🧑", "noun", "m", "È un cittadino italiano.", "He is an Italian citizen."],
      ["la professione", "profession", "💼", "noun", "f", "Qual è la tua professione?", "What is your profession?"],
      ["lo stato civile", "marital status", "💍", "phrase", "", "Indichi il suo stato civile.", "State your marital status."],
      ["nascere", "to be born", "👶", "verb", "", "Sono nato a Stavanger.", "I was born in Stavanger."],
      ["in pensione", "retired", "🧓", "adjective", "", "Mio padre è in pensione.", "My father is retired."],
      ["la carta d'identità", "ID card", "🪪", "noun", "f", "Mostra la tua carta d'identità, per favore.", "Show your ID card, please."],
      ["il sesso", "sex", "⚧️", "noun", "m", "Il modulo chiede il sesso.", "The form asks for sex."],
      ["maggiorenne", "of legal age", "🔞", "phrase", "", "È già maggiorenne.", "She is already of legal age."],
    ],
  },

  {
    theme: "identity", tier: "B1", rows: [
      ["l'origine", "origin", "🌐", "noun", "f", "Le sue origini sono umili.", "His origins are humble."],
      ["l'identità", "identity", "🆔", "noun", "f", "Proteggi la tua identità online.", "Protect your identity online."],
      ["la parentela", "kinship", "🧬", "noun", "f", "Non abbiamo nessuna parentela.", "We have no family relationship."],
      ["l'eredità", "heritage", "🏺", "noun", "f", "L'eredità culturale è ricca.", "The cultural heritage is rich."],
      ["appartenere", "to belong", "🧩", "verb", "", "Appartengo a questo club.", "I belong to this club."],
      ["l'ascendenza", "ancestry", "🌳", "noun", "f", "Ha ascendenze spagnole.", "She has Spanish ancestry."],
      ["il tratto", "trait", "🎭", "noun", "m", "La generosità è il suo tratto migliore.", "Generosity is his best trait."],
      ["la personalità", "personality", "✨", "noun", "f", "Ha una personalità forte.", "She has a strong personality."],
      ["l'usanza", "custom", "🔄", "noun", "f", "È un'usanza di famiglia.", "It's a family custom."],
      ["radicato", "deep-rooted", "🌱", "adjective", "", "È un valore molto radicato.", "It's a deeply rooted value."],
    ],
  },

  // ---- Family ----------------------------------------------------------
  {
    theme: "family", tier: "A1", rows: [
      ["la famiglia", "family", "👪", "noun", "f", "La mia famiglia è grande.", "My family is big."],
      ["il padre", "father", "👨", "noun", "m", "Mio padre cucina molto bene.", "My father cooks very well."],
      ["la madre", "mother", "👩", "noun", "f", "Chiamo mia madre ogni giorno.", "I call my mother every day."],
      ["il figlio", "son", "👦", "noun", "m", "Suo figlio ha cinque anni.", "Her son is five years old."],
      ["la figlia", "daughter", "👧", "noun", "f", "Mia figlia studia musica.", "My daughter studies music."],
      ["il fratello", "brother", "🧑", "noun", "m", "Ho un fratello minore.", "I have a younger brother."],
      ["la sorella", "sister", "👭", "noun", "f", "Mia sorella vive a Roma.", "My sister lives in Rome."],
      ["il nonno", "grandfather", "👴", "noun", "m", "Il nonno racconta storie.", "Grandfather tells stories."],
      ["la nonna", "grandmother", "👵", "noun", "f", "La nonna fa una torta.", "Grandmother makes a cake."],
      ["il bebè", "baby", "👶", "noun", "m", "Il bebè dorme adesso.", "The baby is sleeping now."],
    ],
  },
  {
    theme: "family", tier: "A2", rows: [
      ["lo zio", "uncle", "🧔", "noun", "m", "Mio zio vive in Messico.", "My uncle lives in Mexico."],
      ["la zia", "aunt", "👩", "noun", "f", "Mia zia è medico.", "My aunt is a doctor."],
      ["il cugino", "cousin", "🧑", "noun", "m", "Mio cugino gioca a calcio.", "My cousin plays football."],
      ["il nipote", "nephew", "👦", "noun", "m", "Mi prendo cura di mio nipote il lunedì.", "I look after my nephew on Mondays."],
      ["il nipotino", "grandson", "👶", "noun", "m", "Il suo nipotino è appena nato.", "Her grandson has just been born."],
      ["il coniuge", "husband", "🤵", "noun", "m", "Il suo coniuge lavora fuori.", "Her husband works away."],
      ["la moglie", "wife", "👰", "noun", "f", "Mia moglie parla tre lingue.", "My wife speaks three languages."],
      ["il suocero", "father-in-law", "👴", "noun", "m", "Mio suocero è molto gentile.", "My father-in-law is very kind."],
      ["il cognato", "brother-in-law", "🧔", "noun", "m", "Mio cognato è ingegnere.", "My brother-in-law is an engineer."],
      ["il genero", "son-in-law", "🤵", "noun", "m", "Suo genero cucina la domenica.", "Her son-in-law cooks on Sundays."],
    ],
  },

  {
    theme: "family", tier: "B1", rows: [
      ["l'antenato", "ancestor", "🪦", "noun", "m", "I suoi antenati erano marinai.", "His ancestors were sailors."],
      ["la convivenza", "coexistence", "🤝", "noun", "f", "La convivenza migliora con il rispetto.", "Living together improves with respect."],
      ["il legame", "bond", "🔗", "noun", "m", "C'è un legame forte tra loro.", "There's a strong bond between them."],
      ["crescere", "to raise", "🍼", "verb", "", "Crescono i loro figli con affetto.", "They raise their children with love."],
      ["l'educazione", "upbringing", "👐", "noun", "f", "L'educazione richiede pazienza.", "Upbringing requires patience."],
      ["adottare", "to adopt", "🤱", "verb", "", "Hanno deciso di adottare un bambino.", "They decided to adopt a child."],
      ["la madrina", "godmother", "👼", "noun", "f", "La mia madrina vive vicino.", "My godmother lives nearby."],
      ["ereditare", "to inherit", "📜", "verb", "", "Ha ereditato la casa di sua nonna.", "He inherited his grandmother's house."],
      ["il parente", "relative", "👪", "noun", "m", "Ho parenti in Cile.", "I have relatives in Chile."],
      ["unito", "close-knit", "❤️", "adjective", "", "Siamo una famiglia molto unita.", "We are a very close-knit family."],
    ],
  },

  // ---- House & Home ----------------------------------------------------
  {
    theme: "house", tier: "A1", rows: [
      ["la casa", "house", "🏠", "noun", "f", "Vivo in una casa piccola.", "I live in a small house."],
      ["l'appartamento", "flat", "🏢", "noun", "m", "Il suo appartamento è in centro.", "Her flat is in the centre."],
      ["la cucina", "kitchen", "🍳", "noun", "f", "La cucina è molto luminosa.", "The kitchen is very bright."],
      ["il bagno", "bathroom", "🚿", "noun", "m", "Il bagno è in fondo.", "The bathroom is at the back."],
      ["il letto", "bed", "🛏️", "noun", "m", "Il letto è molto comodo.", "The bed is very comfortable."],
      ["la porta", "door", "🚪", "noun", "f", "Chiudi la porta, per favore.", "Close the door, please."],
      ["la finestra", "window", "🪟", "noun", "f", "Apro la finestra la mattina.", "I open the window in the morning."],
      ["il tavolo", "table", "🍽️", "noun", "m", "Metti i piatti sul tavolo.", "Put the plates on the table."],
      ["la sedia", "chair", "🪑", "noun", "f", "Questa sedia è rotta.", "This chair is broken."],
      ["il divano", "sofa", "🛋️", "noun", "m", "Leggo sul divano la sera.", "I read on the sofa at night."],
    ],
  },
  {
    theme: "house", tier: "A2", rows: [
      ["la camera da letto", "bedroom", "🛌", "noun", "f", "La camera da letto dà sul giardino.", "The bedroom faces the garden."],
      ["il salotto", "living room", "📺", "noun", "m", "Guardiamo la TV in salotto.", "We watch TV in the living room."],
      ["il corridoio", "hallway", "🚪", "noun", "m", "Il corridoio è stretto.", "The hallway is narrow."],
      ["la scala", "stairs", "🪜", "noun", "f", "Salgo le scale.", "I go up the stairs."],
      ["il soffitto", "ceiling", "🏚️", "noun", "m", "Il soffitto è molto alto.", "The ceiling is very high."],
      ["il pavimento", "floor", "🧱", "noun", "m", "Il pavimento è di legno.", "The floor is wooden."],
      ["l'armadio", "wardrobe", "🚪", "noun", "m", "Tengo i vestiti nell'armadio.", "I keep my clothes in the wardrobe."],
      ["il frigorifero", "fridge", "🧊", "noun", "m", "Il latte è nel frigorifero.", "The milk is in the fridge."],
      ["la lampada", "lamp", "💡", "noun", "f", "Accendi la lampada.", "Turn on the lamp."],
      ["il giardino", "garden", "🌷", "noun", "m", "Prendiamo il caffè in giardino.", "We have coffee in the garden."],
    ],
  },

  {
    theme: "house", tier: "B1", rows: [
      ["l'affitto", "rent", "🔑", "noun", "m", "L'affitto aumenta ogni anno.", "The rent goes up every year."],
      ["il mutuo", "mortgage", "🏦", "noun", "m", "Paghiamo il mutuo in trent'anni.", "We pay the mortgage over thirty years."],
      ["l'inquilino", "tenant", "🚪", "noun", "m", "L'inquilino firma il contratto.", "The tenant signs the contract."],
      ["il trasloco", "move", "📦", "noun", "m", "Il trasloco è stato faticoso.", "The move was exhausting."],
      ["arredato", "furnished", "🛋️", "adjective", "", "Affitto un appartamento arredato.", "I rent a furnished flat."],
      ["l'elettrodomestico", "appliance", "🔌", "noun", "m", "Ho comprato un nuovo elettrodomestico.", "I bought a new appliance."],
      ["la facciata", "façade", "🏛️", "noun", "f", "La facciata ha bisogno di una mano di vernice.", "The façade needs paint."],
      ["il ripostiglio", "storage room", "🗄️", "noun", "m", "Tengo le scatole nel ripostiglio.", "I keep the boxes in the storage room."],
      ["accogliente", "cosy", "🕯️", "adjective", "", "Il suo salotto è molto accogliente.", "Her living room is very cosy."],
      ["il riscaldamento", "heating", "🔥", "noun", "m", "Il riscaldamento non funziona.", "The heating isn't working."],
    ],
  },

  // ---- Food & Drink ----------------------------------------------------
  {
    theme: "food", tier: "A1", rows: [
      ["l'acqua", "water", "💧", "noun", "f", "Bevo molta acqua.", "I drink a lot of water."],
      ["il pane", "bread", "🍞", "noun", "m", "Compro pane fresco.", "I buy fresh bread."],
      ["il latte", "milk", "🥛", "noun", "m", "Prendo il caffè con il latte.", "I have coffee with milk."],
      ["il caffè", "coffee", "☕", "noun", "m", "Il caffè è caldo.", "The coffee is hot."],
      ["la frutta", "fruit", "🍎", "noun", "f", "La frutta è molto sana.", "Fruit is very healthy."],
      ["la carne", "meat", "🥩", "noun", "f", "Non mangio carne rossa.", "I don't eat red meat."],
      ["il pesce", "fish", "🐟", "noun", "m", "Il pesce è fresco.", "The fish is fresh."],
      ["l'uovo", "egg", "🥚", "noun", "m", "A colazione mangio un uovo.", "I have an egg for breakfast."],
      ["il formaggio", "cheese", "🧀", "noun", "m", "Mi piace il formaggio stagionato.", "I like strong cheese."],
      ["il cibo", "food", "🍽️", "noun", "m", "Il cibo è pronto.", "The food is ready."],
    ],
  },
  {
    theme: "food", tier: "A2", rows: [
      ["la colazione", "breakfast", "🥐", "noun", "f", "La colazione è alle otto.", "Breakfast is at eight."],
      ["il pranzo", "lunch", "🍲", "noun", "m", "Il pranzo dura un'ora.", "Lunch lasts an hour."],
      ["la cena", "dinner", "🍝", "noun", "f", "Preparo la cena presto.", "I make dinner early."],
      ["la ricetta", "recipe", "📋", "noun", "f", "Seguo la ricetta passo dopo passo.", "I follow the recipe step by step."],
      ["lo zucchero", "sugar", "🍬", "noun", "m", "Non prendo zucchero nel tè.", "I don't take sugar in my tea."],
      ["il sale", "salt", "🧂", "noun", "m", "Manca il sale nella zuppa.", "The soup needs salt."],
      ["l'olio", "oil", "🫒", "noun", "m", "Cucino con l'olio d'oliva.", "I cook with olive oil."],
      ["la verdura", "vegetable", "🥦", "noun", "f", "Mangio verdura ogni giorno.", "I eat vegetables every day."],
      ["il dolce", "dessert", "🍰", "noun", "m", "Vuoi il dolce?", "Would you like dessert?"],
      ["la bevanda", "drink", "🥤", "noun", "f", "Quale bevanda preferisci?", "Which drink do you prefer?"],
    ],
  },

  {
    theme: "food", tier: "B1", rows: [
      ["l'ingrediente", "ingredient", "🧄", "noun", "m", "Manca un ingrediente fondamentale.", "A key ingredient is missing."],
      ["il cucchiaio", "spoonful", "🥄", "noun", "m", "Aggiungi un cucchiaio di miele.", "Add a spoonful of honey."],
      ["bollire", "to boil", "♨️", "verb", "", "Fai bollire l'acqua per due minuti.", "Boil the water for two minutes."],
      ["friggere", "to fry", "🍳", "verb", "", "Friggo le patate nell'olio.", "I fry the potatoes in oil."],
      ["il sapore", "flavour", "👅", "noun", "m", "Questo piatto ha molto sapore.", "This dish has a lot of flavour."],
      ["insipido", "bland", "😐", "adjective", "", "La zuppa è insipida.", "The soup is bland."],
      ["piccante", "spicy", "🌶️", "adjective", "", "Mi piace il cibo piccante.", "I like spicy food."],
      ["la merenda", "afternoon snack", "🍪", "noun", "f", "I bambini fanno merenda.", "The children have their afternoon snack."],
      ["scadere", "to expire", "📆", "verb", "", "Il latte scade domani.", "The milk expires tomorrow."],
      ["il congelatore", "freezer", "❄️", "noun", "m", "Tieni il pesce nel congelatore.", "Keep the fish in the freezer."],
    ],
  },

  // ---- Body & Health ---------------------------------------------------
  {
    theme: "body-health", tier: "A1", rows: [
      ["il corpo", "body", "🧍", "noun", "m", "Il corpo ha bisogno di riposo.", "The body needs rest."],
      ["la testa", "head", "🗣️", "noun", "f", "Mi fa male la testa.", "My head hurts."],
      ["la mano", "hand", "✋", "noun", "f", "Alza la mano.", "Raise your hand."],
      ["l'occhio", "eye", "👁️", "noun", "m", "Ha un occhio nero.", "He has a black eye."],
      ["la bocca", "mouth", "👄", "noun", "f", "Apri la bocca.", "Open your mouth."],
      ["il piede", "foot", "🦶", "noun", "m", "Mi fa male il piede sinistro.", "My left foot hurts."],
      ["il braccio", "arm", "💪", "noun", "m", "Si è rotto il braccio.", "He broke his arm."],
      ["la gamba", "leg", "🦵", "noun", "f", "Stendi la gamba.", "Stretch your leg."],
      ["i capelli", "hair", "💇", "noun", "m", "Ha i capelli lunghi.", "She has long hair."],
      ["il naso", "nose", "👃", "noun", "m", "Mi prude il naso.", "My nose is itchy."],
    ],
  },
  {
    theme: "body-health", tier: "A2", rows: [
      ["la salute", "health", "❤️‍🩹", "noun", "f", "Prenditi cura della tua salute.", "Take care of your health."],
      ["la malattia", "illness", "🤒", "noun", "f", "È una malattia comune.", "It is a common illness."],
      ["il dolore", "pain", "😣", "noun", "m", "Sento un dolore forte.", "I feel a strong pain."],
      ["la febbre", "fever", "🌡️", "noun", "f", "Il bambino ha la febbre.", "The child has a fever."],
      ["la pastiglia", "pill", "💊", "noun", "f", "Prendo una pastiglia al giorno.", "I take one pill a day."],
      ["il medico", "doctor", "🩺", "noun", "m", "Vado dal medico domani.", "I'm going to the doctor tomorrow."],
      ["l'ospedale", "hospital", "🏥", "noun", "m", "L'ospedale è vicino.", "The hospital is nearby."],
      ["la visita medica", "medical appointment", "📅", "phrase", "", "Ho una visita medica venerdì.", "I have a medical appointment on Friday."],
      ["tossire", "to cough", "😷", "verb", "", "Non smetto di tossire.", "I can't stop coughing."],
      ["sano", "healthy", "🥗", "adjective", "", "Conduce una vita sana.", "She leads a healthy life."],
    ],
  },

  {
    theme: "body-health", tier: "B1", rows: [
      ["il sintomo", "symptom", "🤒", "noun", "m", "Il sintomo principale è la tosse.", "The main symptom is a cough."],
      ["la ricetta medica", "prescription", "📝", "phrase", "", "Ho bisogno di una ricetta medica.", "I need a prescription."],
      ["il trattamento", "treatment", "💉", "noun", "m", "Il trattamento dura un mese.", "The treatment lasts a month."],
      ["la ferita", "wound", "🩹", "noun", "f", "Pulisci la ferita con attenzione.", "Clean the wound carefully."],
      ["riprendersi", "to recover", "🌤️", "verb", "", "Si riprende a poco a poco.", "He's recovering little by little."],
      ["il battito", "heartbeat", "💓", "noun", "m", "Sento il battito del cuore.", "I feel the heartbeat."],
      ["stordito", "dizzy", "😵", "adjective", "", "Mi sento stordito quando mi alzo.", "I feel dizzy when I get up."],
      ["l'operazione", "surgery", "🏥", "noun", "f", "L'operazione è andata bene.", "The surgery went well."],
      ["il benessere", "wellbeing", "🧘", "noun", "m", "Lo sport migliora il benessere.", "Sport improves wellbeing."],
      ["starnutire", "to sneeze", "🤧", "verb", "", "Non smette di starnutire.", "He won't stop sneezing."],
    ],
  },

  // ---- Daily Routine ---------------------------------------------------
  {
    theme: "daily-routine", tier: "A1", rows: [
      ["svegliarsi", "to wake up", "⏰", "verb", "", "Mi sveglio alle sette.", "I wake up at seven."],
      ["alzarsi", "to get up", "🛌", "verb", "", "Mi alzo subito.", "I get up right away."],
      ["fare la doccia", "to shower", "🚿", "verb", "", "Mi faccio la doccia la mattina.", "I shower in the morning."],
      ["vestirsi", "to get dressed", "👕", "verb", "", "Mi vesto in fretta.", "I get dressed quickly."],
      ["fare colazione", "to have breakfast", "🥣", "verb", "", "Faccio colazione a casa.", "I have breakfast at home."],
      ["la routine", "routine", "🔁", "noun", "f", "Ho una routine fissa.", "I have a fixed routine."],
      ["l'orario", "schedule", "🗓️", "noun", "m", "Il mio orario cambia ogni settimana.", "My schedule changes every week."],
      ["presto", "early", "🌅", "adverb", "", "Vado a letto presto.", "I go to bed early."],
      ["tardi", "late", "🌙", "adverb", "", "Arrivo tardi al lavoro.", "I arrive late to work."],
      ["ogni giorno", "every day", "📆", "phrase", "", "Studio italiano ogni giorno.", "I study Italian every day."],
    ],
  },
  {
    theme: "daily-routine", tier: "A2", rows: [
      ["andare a letto", "to go to bed", "🛏️", "verb", "", "Vado a letto a mezzanotte.", "I go to bed at midnight."],
      ["pettinarsi", "to comb one's hair", "💇", "verb", "", "Mi pettino prima di uscire.", "I comb my hair before leaving."],
      ["lavarsi i denti", "to brush", "🪥", "verb", "", "Mi lavo i denti.", "I brush my teeth."],
      ["lavarsi", "to wash oneself", "🧼", "verb", "", "Mi lavo le mani.", "I wash my hands."],
      ["riposare", "to rest", "🛋️", "verb", "", "Riposo un po' dopo pranzo.", "I rest a while after eating."],
      ["le faccende", "chores", "🧹", "noun", "f", "Faccio le faccende il sabato.", "I do the chores on Saturdays."],
      ["essere solito", "to usually do", "🔁", "verb", "", "Sono solito correre il pomeriggio.", "I usually run in the afternoon."],
      ["occupato", "busy", "📚", "adjective", "", "Oggi sono molto occupato.", "Today I am very busy."],
      ["libero", "free", "🆓", "adjective", "", "Ho il pomeriggio libero.", "I have the afternoon free."],
      ["mentre", "while", "⏳", "conjunction", "", "Cucino mentre ascolto la musica.", "I cook while I listen to music."],
    ],
  },

  {
    theme: "daily-routine", tier: "B1", rows: [
      ["alzarsi presto", "to get up early", "🌄", "verb", "", "Mi alzo presto per andare in palestra.", "I get up early to go to the gym."],
      ["la levataccia", "early rise", "⏰", "noun", "f", "Che levataccia stamattina!", "What an early start this morning."],
      ["fare le ore piccole", "to stay up late", "🌃", "verb", "", "Non mi piace fare le ore piccole.", "I don't like staying up late."],
      ["il pisolino", "nap", "😴", "noun", "m", "Faccio un pisolino breve.", "I take a short nap."],
      ["la mansione", "task", "🧺", "noun", "f", "Ho molte mansioni oggi.", "I have many tasks today."],
      ["sfruttare", "to make the most of", "☀️", "verb", "", "Sfrutto la mattina per studiare.", "I make the most of the morning to study."],
      ["il trambusto", "hustle and bustle", "🏃", "noun", "m", "Il trambusto quotidiano mi stanca.", "The daily hustle tires me out."],
      ["rilassarsi", "to relax", "🧖", "verb", "", "Mi rilasso con un bagno caldo.", "I relax with a hot bath."],
      ["quotidiano", "everyday", "📅", "adjective", "", "Fa parte della mia vita quotidiana.", "It's part of my everyday life."],
      ["puntuale", "punctual", "⏱️", "adjective", "", "È sempre molto puntuale.", "He's always very punctual."],
    ],
  },

  // ---- Work & Study ----------------------------------------------------
  {
    theme: "work-study", tier: "A1", rows: [
      ["il lavoro", "work", "💼", "noun", "m", "Vado al lavoro in treno.", "I go to work by train."],
      ["la scuola", "school", "🏫", "noun", "f", "I bambini vanno a scuola.", "The children go to school."],
      ["il professore", "teacher", "👨‍🏫", "noun", "m", "Il professore spiega bene.", "The teacher explains well."],
      ["lo studente", "student", "🎓", "noun", "m", "Sono uno studente di medicina.", "I am a medical student."],
      ["l'ufficio", "office", "🏢", "noun", "m", "L'ufficio è chiuso.", "The office is closed."],
      ["il libro", "book", "📖", "noun", "m", "Leggo un libro interessante.", "I'm reading an interesting book."],
      ["il computer", "computer", "💻", "noun", "m", "Lavoro al computer.", "I work with the computer."],
      ["studiare", "to study", "📚", "verb", "", "Studio di sera.", "I study at night."],
      ["imparare", "to learn", "🧠", "verb", "", "Voglio imparare l'italiano.", "I want to learn Italian."],
      ["l'esame", "exam", "📝", "noun", "m", "L'esame è difficile.", "The exam is difficult."],
    ],
  },
  {
    theme: "work-study", tier: "A2", rows: [
      ["l'azienda", "company", "🏢", "noun", "f", "Lavoro in una grande azienda.", "I work at a big company."],
      ["il capo", "boss", "🧑‍💼", "noun", "m", "Il mio capo è molto giusto.", "My boss is very fair."],
      ["lo stipendio", "salary", "💶", "noun", "m", "Lo stipendio è buono.", "The salary is good."],
      ["la riunione", "meeting", "📊", "noun", "f", "La riunione è alle dieci.", "The meeting is at ten."],
      ["l'orario di lavoro", "working hours", "🕘", "phrase", "", "Il mio orario di lavoro è flessibile.", "My working hours are flexible."],
      ["la laurea", "degree", "🎓", "noun", "f", "Ho conseguito una laurea tecnica.", "I earned a technical degree."],
      ["il progetto", "project", "📁", "noun", "m", "Il progetto va bene.", "The project is going well."],
      ["assumere", "to hire", "🤝", "verb", "", "Assumeranno due persone.", "They are going to hire two people."],
      ["il collega", "colleague", "👥", "noun", "m", "Il mio collega è del Perù.", "My colleague is from Peru."],
      ["il colloquio", "interview", "🗣️", "noun", "m", "Ho un colloquio lunedì.", "I have an interview on Monday."],
    ],
  },

  {
    theme: "work-study", tier: "B1", rows: [
      ["la promozione", "promotion", "📈", "noun", "f", "Gli hanno dato una promozione.", "They gave him a promotion."],
      ["la giornata", "workday", "🕘", "noun", "f", "Ho una giornata lunga.", "I have a long workday."],
      ["la scadenza", "deadline", "⏳", "noun", "f", "La scadenza è venerdì.", "The deadline ends on Friday."],
      ["la borsa di studio", "scholarship", "🎓", "noun", "f", "Ho ottenuto una borsa di studio.", "I got a scholarship."],
      ["svolgere", "to carry out", "🛠️", "verb", "", "Svolge un ruolo importante.", "He plays an important role."],
      ["il curriculum", "CV", "📄", "noun", "m", "Aggiorno il mio curriculum.", "I'm updating my CV."],
      ["la formazione", "training", "📚", "noun", "f", "L'azienda offre formazione.", "The company offers training."],
      ["il licenziamento", "dismissal", "📉", "noun", "m", "Il licenziamento è stato una sorpresa.", "The dismissal was a surprise."],
      ["intraprendere", "to start a venture", "🚀", "verb", "", "Vuole intraprendere un'attività.", "She wants to start a business."],
      ["il personale", "staff", "👥", "noun", "m", "Il personale è cresciuto quest'anno.", "The staff grew this year."],
    ],
  },

  // ---- Travel ----------------------------------------------------------
  {
    theme: "travel", tier: "A1", rows: [
      ["il viaggio", "trip", "🧳", "noun", "m", "Il viaggio è stato lungo.", "The trip was long."],
      ["l'aereo", "plane", "✈️", "noun", "m", "L'aereo parte alle sei.", "The plane leaves at six."],
      ["il treno", "train", "🚆", "noun", "m", "Prendo il treno ogni mattina.", "I take the train every morning."],
      ["la macchina", "car", "🚗", "noun", "f", "Andiamo in macchina.", "We're going by car."],
      ["l'hotel", "hotel", "🏨", "noun", "m", "L'hotel è sulla spiaggia.", "The hotel is on the beach."],
      ["il biglietto", "ticket", "🎫", "noun", "m", "Compro il biglietto su internet.", "I buy the ticket online."],
      ["la valigia", "suitcase", "🧳", "noun", "f", "Faccio la valigia stasera.", "I'll pack the suitcase tonight."],
      ["l'aeroporto", "airport", "🛫", "noun", "m", "Arrivo in aeroporto presto.", "I arrive at the airport early."],
      ["viaggiare", "to travel", "🌍", "verb", "", "Adoro viaggiare da solo.", "I love travelling alone."],
      ["la mappa", "map", "🗺️", "noun", "f", "Guardo la mappa della città.", "I look at the city map."],
    ],
  },
  {
    theme: "travel", tier: "A2", rows: [
      ["il passaporto", "passport", "🛂", "noun", "m", "Il mio passaporto scade presto.", "My passport expires soon."],
      ["la frontiera", "border", "🚧", "noun", "f", "Attraversiamo la frontiera di notte.", "We cross the border at night."],
      ["la stazione", "station", "🚉", "noun", "f", "La stazione è lontana.", "The station is far away."],
      ["il bagaglio", "luggage", "🧳", "noun", "m", "Imbarco il bagaglio.", "I check in the luggage."],
      ["la destinazione", "destination", "📍", "noun", "f", "La nostra destinazione è Firenze.", "Our destination is Florence."],
      ["prenotare", "to book", "📅", "verb", "", "Prenoto una camera doppia.", "I book a double room."],
      ["il volo", "flight", "🛬", "noun", "m", "Il volo è in ritardo.", "The flight is delayed."],
      ["l'arrivo", "arrival", "🛬", "noun", "m", "L'arrivo è alle nove.", "Arrival is at nine."],
      ["l'uscita", "departure", "🚪", "noun", "f", "L'uscita è a destra.", "The exit is on the right."],
      ["il turista", "tourist", "📸", "noun", "m", "Il turista scatta foto.", "The tourist takes photos."],
    ],
  },

  {
    theme: "travel", tier: "B1", rows: [
      ["l'alloggio", "accommodation", "🏨", "noun", "m", "L'alloggio include la colazione.", "The accommodation includes breakfast."],
      ["lo scalo", "stopover", "🛑", "noun", "m", "Il volo ha uno scalo.", "The flight has a stopover."],
      ["l'itinerario", "itinerary", "🗺️", "noun", "m", "Pianifico l'itinerario con cura.", "I plan the itinerary carefully."],
      ["l'assicurazione di viaggio", "travel insurance", "🛡️", "phrase", "", "Stipulo un'assicurazione di viaggio.", "I take out travel insurance."],
      ["imbarcarsi", "to board", "🛫", "verb", "", "Ci imbarchiamo tra mezz'ora.", "We board in half an hour."],
      ["la dogana", "customs", "🛂", "noun", "f", "Passiamo per la dogana.", "We go through customs."],
      ["il tragitto", "journey", "🚌", "noun", "m", "Il tragitto dura tre ore.", "The journey takes three hours."],
      ["alloggiare", "to stay", "🛏️", "verb", "", "Alloggiamo in un ostello.", "We stay in a guesthouse."],
      ["la bussola", "compass", "🧭", "noun", "f", "Porto una bussola nello zaino.", "I carry a compass in my backpack."],
      ["lo zaino", "backpack", "🎒", "noun", "m", "Preparo lo zaino per il viaggio.", "I pack the backpack for the trip."],
    ],
  },

  // ---- Time & Weather --------------------------------------------------
  {
    theme: "time-weather", tier: "A1", rows: [
      ["il tempo", "weather", "🌤️", "noun", "m", "Oggi fa bel tempo.", "The weather is nice today."],
      ["il sole", "sun", "☀️", "noun", "m", "C'è il sole stamattina.", "It's sunny this morning."],
      ["la pioggia", "rain", "🌧️", "noun", "f", "La pioggia non smette.", "The rain won't stop."],
      ["il vento", "wind", "💨", "noun", "m", "C'è molto vento.", "It's very windy."],
      ["la neve", "snow", "❄️", "noun", "f", "La neve copre il campo.", "The snow covers the field."],
      ["il giorno", "day", "📆", "noun", "m", "È un giorno splendido.", "It's a lovely day."],
      ["la notte", "night", "🌙", "noun", "f", "La notte è fredda.", "The night is cold."],
      ["oggi", "today", "📅", "adverb", "", "Oggi non lavoro.", "I'm not working today."],
      ["domani", "tomorrow", "🌅", "adverb", "", "Domani andiamo al cinema.", "Tomorrow we're going to the cinema."],
      ["fa caldo", "it's hot", "🥵", "phrase", "", "Ad agosto fa caldo.", "In August it's hot."],
    ],
  },
  {
    theme: "time-weather", tier: "A2", rows: [
      ["la temperatura", "temperature", "🌡️", "noun", "f", "La temperatura scende di notte.", "The temperature drops at night."],
      ["la nuvola", "cloud", "☁️", "noun", "f", "C'è una nuvola nera.", "There's a black cloud."],
      ["il temporale", "storm", "⛈️", "noun", "m", "Sta arrivando un forte temporale.", "A strong storm is coming."],
      ["il grado", "degree", "🔆", "noun", "m", "Ci sono trenta gradi.", "It's thirty degrees."],
      ["il bollettino meteo", "forecast", "📰", "noun", "m", "Il bollettino meteo annuncia pioggia.", "The forecast predicts rain."],
      ["sereno", "clear", "🌞", "adjective", "", "Il cielo è sereno.", "The sky is clear."],
      ["umido", "humid", "💦", "adjective", "", "Il clima è umido qui.", "The climate is humid here."],
      ["la stagione dell'anno", "season", "🍂", "phrase", "", "L'autunno è la mia stagione dell'anno preferita.", "Autumn is my favourite season."],
      ["l'alba", "dawn", "🌄", "noun", "f", "Partiamo all'alba.", "We leave at dawn."],
      ["fa freddo", "it's cold", "🥶", "phrase", "", "In inverno fa freddo.", "In winter it's cold."],
    ],
  },

  {
    theme: "time-weather", tier: "B1", rows: [
      ["il rovescio", "shower", "🌦️", "noun", "m", "Cadono rovesci nel pomeriggio.", "Showers fall in the afternoon."],
      ["la nebbia", "fog", "🌫️", "noun", "f", "C'è nebbia in strada.", "There's fog on the road."],
      ["la grandine", "hail", "🌨️", "noun", "f", "La grandine ha danneggiato le auto.", "The hail damaged the cars."],
      ["soleggiato", "sunny", "🌞", "adjective", "", "Domani sarà una giornata soleggiata.", "Tomorrow will be a sunny day."],
      ["nuvoloso", "cloudy", "☁️", "adjective", "", "Il cielo è nuvoloso.", "The sky is cloudy."],
      ["la siccità", "drought", "🏜️", "noun", "f", "La siccità colpisce la campagna.", "The drought affects the countryside."],
      ["il lampo", "lightning", "⚡", "noun", "m", "Abbiamo visto un lampo lontano.", "We saw distant lightning."],
      ["gelare", "to freeze", "🧊", "verb", "", "Ieri notte ha gelato nella valle.", "Last night it froze in the valley."],
      ["il tramonto", "dusk", "🌇", "noun", "m", "Passeggiamo al tramonto.", "We walk at dusk."],
      ["mite", "mild", "🌡️", "adjective", "", "Il clima è mite in autunno.", "The climate is mild in autumn."],
    ],
  },

  // ---- Shopping --------------------------------------------------------
  {
    theme: "shopping", tier: "A1", rows: [
      ["il negozio", "shop", "🏪", "noun", "m", "Il negozio apre alle nove.", "The shop opens at nine."],
      ["il denaro", "money", "💶", "noun", "m", "Non ho spiccioli.", "I don't have any loose change."],
      ["il prezzo", "price", "🏷️", "noun", "m", "Il prezzo è molto alto.", "The price is very high."],
      ["comprare", "to buy", "🛒", "verb", "", "Voglio comprare delle scarpe.", "I want to buy shoes."],
      ["pagare", "to pay", "💳", "verb", "", "Pago con la carta.", "I pay by card."],
      ["caro", "expensive", "💸", "adjective", "", "Questo orologio è molto caro.", "This watch is very expensive."],
      ["economico", "cheap", "🪙", "adjective", "", "La frutta è economica oggi.", "Fruit is cheap today."],
      ["l'euro", "euro", "💶", "noun", "m", "Costa cinque euro.", "It costs five euros."],
      ["i vestiti", "clothes", "👕", "noun", "m", "Compro vestiti invernali.", "I buy winter clothes."],
      ["il mercato", "market", "🧺", "noun", "m", "Vado al mercato il sabato.", "I go to the market on Saturdays."],
    ],
  },
  {
    theme: "shopping", tier: "A2", rows: [
      ["l'offerta", "offer", "🏷️", "noun", "f", "C'è un'offerta questa settimana.", "There's an offer this week."],
      ["lo sconto", "discount", "🔖", "noun", "m", "Mi fanno uno sconto.", "They give me a discount."],
      ["la taglia", "size", "📏", "noun", "f", "Che taglia porti?", "What size do you wear?"],
      ["lo scontrino", "receipt", "🧾", "noun", "m", "Conserva lo scontrino.", "Keep the receipt."],
      ["la cassa", "checkout", "💰", "noun", "f", "Paghi alla cassa, per favore.", "Pay at the checkout, please."],
      ["spendere", "to spend", "💸", "verb", "", "Spendo poco in vestiti.", "I spend little on clothes."],
      ["restituire", "to return", "↩️", "verb", "", "Voglio restituire questa camicia.", "I want to return this shirt."],
      ["il cliente", "customer", "🧑", "noun", "m", "Il cliente chiede sempre.", "The customer always asks."],
      ["provare", "to try on", "👗", "verb", "", "Provo il vestito.", "I try on the dress."],
      ["la vetrina", "shop window", "🪟", "noun", "f", "Guardo la vetrina.", "I look at the shop window."],
    ],
  },

  {
    theme: "shopping", tier: "B1", rows: [
      ["il budget", "budget", "💰", "noun", "m", "Ho un budget limitato.", "I have a tight budget."],
      ["la fattura", "invoice", "🧾", "noun", "f", "Chiedo la fattura, per favore.", "I ask for the invoice, please."],
      ["il rimborso", "refund", "💵", "noun", "m", "Chiedo un rimborso.", "I request a refund."],
      ["contrattare", "to haggle", "🤝", "verb", "", "È normale contrattare al mercato.", "It's normal to haggle in the market."],
      ["esaurito", "sold out", "🚫", "adjective", "", "Il prodotto è esaurito.", "The product is sold out."],
      ["la garanzia", "warranty", "📑", "noun", "f", "La garanzia dura due anni.", "The warranty lasts two years."],
      ["il commercio", "business", "🏬", "noun", "m", "Il commercio locale resiste.", "Local business holds on."],
      ["accessibile", "affordable", "🪙", "adjective", "", "È un prezzo accessibile.", "It's an affordable price."],
      ["la spedizione", "delivery", "📦", "noun", "f", "La spedizione è gratuita.", "Delivery is free."],
      ["la marca", "brand", "🏷️", "noun", "f", "Preferisco questa marca.", "I prefer this brand."],
    ],
  },

  // ---- Town & Directions -----------------------------------------------
  {
    theme: "town", tier: "A1", rows: [
      ["la città", "city", "🏙️", "noun", "f", "Vivo in una grande città.", "I live in a big city."],
      ["il paesino", "town", "🏘️", "noun", "m", "Il mio paesino è molto tranquillo.", "My town is very quiet."],
      ["la strada", "street", "🛣️", "noun", "f", "La strada è vuota.", "The street is empty."],
      ["la piazza", "square", "⛲", "noun", "f", "Ci vediamo in piazza.", "Let's meet in the square."],
      ["il parco", "park", "🌳", "noun", "m", "Passeggio nel parco.", "I walk through the park."],
      ["la banca", "bank", "🏦", "noun", "f", "La banca chiude alle due.", "The bank closes at two."],
      ["il ristorante", "restaurant", "🍴", "noun", "m", "Il ristorante è nuovo.", "The restaurant is new."],
      ["la chiesa", "church", "⛪", "noun", "f", "La chiesa è del Quattrocento.", "The church is from the 15th century."],
      ["il museo", "museum", "🏛️", "noun", "m", "Il museo apre la domenica.", "The museum opens on Sunday."],
      ["il supermercato", "supermarket", "🛒", "noun", "m", "Vado al supermercato a piedi.", "I walk to the supermarket."],
    ],
  },
  {
    theme: "town", tier: "A2", rows: [
      ["l'angolo", "corner", "📐", "noun", "m", "Gira all'angolo.", "Turn at the corner."],
      ["il semaforo", "traffic light", "🚦", "noun", "m", "Fermati al semaforo.", "Stop at the traffic light."],
      ["il ponte", "bridge", "🌉", "noun", "m", "Attraversa il ponte.", "Cross the bridge."],
      ["il marciapiede", "pavement", "🚶", "noun", "m", "Cammina sul marciapiede.", "Walk on the pavement."],
      ["il municipio", "town hall", "🏛️", "noun", "m", "Il municipio è in centro.", "The town hall is in the centre."],
      ["girare", "to turn", "↩️", "verb", "", "Gira a sinistra.", "Turn left."],
      ["andare dritto", "to go straight", "⬆️", "phrase", "", "Vai dritto per due strade.", "Go straight on for two streets."],
      ["la fermata", "stop", "🚏", "noun", "f", "La fermata è proprio qui.", "The stop is right here."],
      ["il quartiere", "neighbourhood", "🏘️", "noun", "m", "Il mio quartiere è molto sicuro.", "My neighbourhood is very safe."],
      ["attraversare", "to cross", "🚸", "verb", "", "Attraversa con prudenza.", "Cross carefully."],
    ],
  },

  {
    theme: "town", tier: "B1", rows: [
      ["il centro storico", "old town", "🏰", "phrase", "", "Il centro storico è bellissimo.", "The old town is beautiful."],
      ["i sobborghi", "outskirts", "🌆", "noun", "m", "Vivono nei sobborghi.", "They live on the outskirts."],
      ["il pedone", "pedestrian", "🚶", "noun", "m", "Il pedone ha la precedenza.", "The pedestrian has priority."],
      ["la rotatoria", "roundabout", "🔄", "noun", "f", "Prendi la seconda uscita alla rotatoria.", "Take the second exit at the roundabout."],
      ["l'illuminazione", "street lighting", "💡", "noun", "f", "L'illuminazione è nuova.", "The street lighting is new."],
      ["affollato", "crowded", "👥", "adjective", "", "È una zona molto affollata.", "It's a very crowded area."],
      ["l'edicola", "kiosk", "🗞️", "noun", "f", "Compro il giornale all'edicola.", "I buy the newspaper at the kiosk."],
      ["il complesso residenziale", "housing estate", "🏘️", "noun", "m", "Vivo in un tranquillo complesso residenziale.", "I live in a quiet housing estate."],
      ["pedonale", "pedestrian", "🚸", "adjective", "", "È una strada pedonale.", "It's a pedestrian street."],
      ["il commissariato", "police station", "🚓", "noun", "m", "Il commissariato è in piazza.", "The police station is on the square."],
    ],
  },

  // ---- Leisure ---------------------------------------------------------
  {
    theme: "leisure", tier: "A1", rows: [
      ["la musica", "music", "🎵", "noun", "f", "Ascolto musica classica.", "I listen to classical music."],
      ["il cinema", "cinema", "🎬", "noun", "m", "Andiamo al cinema venerdì.", "We're going to the cinema on Friday."],
      ["lo sport", "sport", "🏃", "noun", "m", "Faccio sport tre volte alla settimana.", "I do sport three times a week."],
      ["la festa", "party", "🎉", "noun", "f", "La festa è a casa mia.", "The party is at my house."],
      ["il calcio", "football", "⚽", "noun", "m", "Il calcio è molto popolare.", "Football is very popular."],
      ["ballare", "to dance", "💃", "verb", "", "Mi piace ballare la salsa.", "I like to dance salsa."],
      ["cantare", "to sing", "🎤", "verb", "", "Canta molto bene.", "She sings very well."],
      ["leggere", "to read", "📖", "verb", "", "Leggo prima di dormire.", "I read before sleeping."],
      ["giocare", "to play", "🎮", "verb", "", "Gioco con i miei amici.", "I play with my friends."],
      ["nuotare", "to swim", "🏊", "verb", "", "Nuoto nel mare d'estate.", "I swim in the sea in summer."],
    ],
  },
  {
    theme: "leisure", tier: "A2", rows: [
      ["il concerto", "concert", "🎶", "noun", "m", "Il concerto inizia tardi.", "The concert starts late."],
      ["il biglietto d'ingresso", "ticket", "🎟️", "noun", "m", "Il biglietto d'ingresso costa venti euro.", "The ticket costs twenty euros."],
      ["la squadra", "team", "👕", "noun", "f", "La mia squadra ha vinto la partita.", "My team won the match."],
      ["il tifoso", "fan", "📣", "noun", "m", "È un tifoso di basket.", "He is a basketball fan."],
      ["la passione", "hobby", "🎨", "noun", "f", "La mia passione è la fotografia.", "My hobby is photography."],
      ["lo spettacolo", "show", "🎭", "noun", "m", "Lo spettacolo è stato incredibile.", "The show was amazing."],
      ["vedersi", "to meet up", "🤝", "verb", "", "Ci vediamo alle sette.", "Let's meet up at seven."],
      ["godersi", "to enjoy", "😄", "verb", "", "Mi godo i fine settimana.", "I enjoy the weekends."],
      ["lo svago", "leisure", "🛋️", "noun", "m", "Dedico poco tempo allo svago.", "I devote little time to leisure."],
      ["divertirsi", "to have a good time", "🥳", "phrase", "", "Ci siamo divertiti molto ieri sera.", "We had a great time last night."],
    ],
  },

  {
    theme: "leisure", tier: "B1", rows: [
      ["il passatempo", "pastime", "🎲", "noun", "m", "Collezionare francobolli è il suo passatempo.", "Collecting stamps is his pastime."],
      ["l'anteprima", "premiere", "🎬", "noun", "f", "Andiamo all'anteprima stasera.", "We're going to the premiere tonight."],
      ["fare le prove", "to rehearse", "🎻", "verb", "", "Facciamo le prove il martedì.", "We rehearse on Tuesdays."],
      ["la biglietteria", "box office", "🎟️", "noun", "f", "Compro i biglietti alla biglietteria.", "I buy the tickets at the box office."],
      ["scommettere", "to bet", "🎰", "verb", "", "Non mi piace scommettere soldi.", "I don't like betting money."],
      ["gli scacchi", "chess", "♟️", "noun", "m", "Gioco a scacchi con mio nonno.", "I play chess with my grandfather."],
      ["l'escursionismo", "hiking", "🥾", "noun", "m", "L'escursionismo mi rilassa.", "Hiking relaxes me."],
      ["collezionare", "to collect", "🗂️", "verb", "", "Colleziona monete antiche.", "He collects old coins."],
      ["lo spettatore", "spectator", "👀", "noun", "m", "Gli spettatori hanno applaudito.", "The spectators applauded."],
      ["divertente", "entertaining", "😄", "adjective", "", "Il film è molto divertente.", "The film is very entertaining."],
    ],
  },

  // ---- Feelings --------------------------------------------------------
  {
    theme: "feelings", tier: "A1", rows: [
      ["felice", "happy", "😊", "adjective", "", "Oggi sono molto felice.", "I'm very happy today."],
      ["triste", "sad", "😢", "adjective", "", "Si sente triste.", "He feels sad."],
      ["stanco", "tired", "😫", "adjective", "", "Sono stanco per il viaggio.", "I'm tired from the trip."],
      ["contento", "glad", "🙂", "adjective", "", "Siamo contenti della casa.", "We're glad with the house."],
      ["arrabbiato", "angry", "😠", "adjective", "", "È arrabbiato con me.", "He's angry with me."],
      ["nervoso", "nervous", "😬", "adjective", "", "Sono nervoso per l'esame.", "I'm nervous about the exam."],
      ["la paura", "fear", "😨", "noun", "f", "Ho paura del buio.", "I'm afraid of the dark."],
      ["la gioia", "joy", "😁", "noun", "f", "Ho provato una grande gioia.", "I felt great joy."],
      ["annoiato", "bored", "🥱", "adjective", "", "Sono annoiato a casa.", "I'm bored at home."],
      ["avere fame", "to be hungry", "🍽️", "phrase", "", "Ho molta fame.", "I'm very hungry."],
    ],
  },
  {
    theme: "feelings", tier: "A2", rows: [
      ["emozionato", "excited", "🤩", "adjective", "", "Sono emozionato per il viaggio.", "I'm excited about the trip."],
      ["preoccupato", "worried", "😟", "adjective", "", "È preoccupata per suo figlio.", "She's worried about her son."],
      ["sorpreso", "surprised", "😲", "adjective", "", "Sono rimasto sorpreso.", "I was surprised."],
      ["orgoglioso", "proud", "😌", "adjective", "", "Sono orgoglioso di te.", "I'm proud of you."],
      ["imbarazzato", "embarrassed", "😳", "adjective", "", "Si è sentito imbarazzato.", "He felt embarrassed."],
      ["la gelosia", "jealousy", "😒", "noun", "f", "La gelosia non aiuta.", "Jealousy doesn't help."],
      ["il morale", "mood", "💪", "noun", "m", "Oggi ho il morale alto.", "Today I'm in good spirits."],
      ["sentirsi", "to feel", "🫀", "verb", "", "Mi sento molto meglio.", "I feel much better."],
      ["sentire la mancanza", "to miss", "💔", "phrase", "", "Sento la mancanza della mia famiglia.", "I miss my family."],
      ["essere di buon umore", "to be in a good mood", "😄", "phrase", "", "Oggi sono di buon umore.", "Today I'm in a good mood."],
    ],
  },

  {
    theme: "feelings", tier: "B1", rows: [
      ["l'angoscia", "anguish", "😰", "noun", "f", "Ho provato un'angoscia profonda.", "I felt deep anguish."],
      ["il sollievo", "relief", "😮‍💨", "noun", "m", "Che sollievo finire l'esame.", "What a relief to finish the exam."],
      ["spaventato", "scared", "😱", "adjective", "", "Il bambino è spaventato.", "The child is scared."],
      ["la fiducia", "trust", "🤝", "noun", "f", "Ho piena fiducia in te.", "I have full trust in you."],
      ["farsi coraggio", "to cheer up", "🙂", "verb", "", "Fatti coraggio, andrà tutto bene.", "Cheer up, everything will be fine."],
      ["la delusione", "disappointment", "💧", "noun", "f", "È stata una grande delusione.", "It was a big disappointment."],
      ["oberato", "overwhelmed", "😩", "adjective", "", "Sono oberato di lavoro.", "I'm overwhelmed with work."],
      ["l'affetto", "affection", "🥰", "noun", "m", "Provo molto affetto per lui.", "I'm very fond of him."],
      ["arrabbiarsi", "to get angry", "😤", "verb", "", "Si arrabbia per tutto.", "He gets angry about everything."],
      ["fiducioso", "hopeful", "🌈", "adjective", "", "Sono fiducioso per il futuro.", "I'm hopeful about the future."],
    ],
  },

  // ---- Nature ----------------------------------------------------------
  {
    theme: "nature", tier: "A1", rows: [
      ["l'albero", "tree", "🌳", "noun", "m", "L'albero dà molta ombra.", "The tree gives a lot of shade."],
      ["il fiore", "flower", "🌸", "noun", "m", "Il fiore profuma.", "The flower smells nice."],
      ["il mare", "sea", "🌊", "noun", "m", "Il mare è calmo.", "The sea is calm."],
      ["la montagna", "mountain", "⛰️", "noun", "f", "Saliamo la montagna.", "We climb the mountain."],
      ["il fiume", "river", "🏞️", "noun", "m", "Il fiume attraversa il paese.", "The river runs through the town."],
      ["l'animale", "animal", "🐾", "noun", "m", "È un animale selvatico.", "It's a wild animal."],
      ["il cane", "dog", "🐕", "noun", "m", "Il cane gioca in giardino.", "The dog plays in the garden."],
      ["il gatto", "cat", "🐈", "noun", "m", "Il gatto dorme tutto il giorno.", "The cat sleeps all day."],
      ["il cielo", "sky", "🌌", "noun", "m", "Il cielo è azzurro.", "The sky is blue."],
      ["la spiaggia", "beach", "🏖️", "noun", "f", "Andiamo in spiaggia.", "We're going to the beach."],
    ],
  },
  {
    theme: "nature", tier: "A2", rows: [
      ["il bosco", "forest", "🌲", "noun", "m", "Il bosco è molto fitto.", "The forest is very dense."],
      ["la foglia", "leaf", "🍃", "noun", "f", "Cade una foglia dall'albero.", "A leaf falls from the tree."],
      ["la pianta", "plant", "🪴", "noun", "f", "Innaffio la pianta ogni giorno.", "I water the plant every day."],
      ["la campagna", "countryside", "🌾", "noun", "f", "Passiamo la giornata in campagna.", "We spend the day in the countryside."],
      ["l'isola", "island", "🏝️", "noun", "f", "L'isola è molto piccola.", "The island is very small."],
      ["l'uccello", "bird", "🐦", "noun", "m", "L'uccello canta all'alba.", "The bird sings at dawn."],
      ["l'ambiente", "environment", "🌍", "phrase", "", "Bisogna prendersi cura dell'ambiente.", "We must take care of the environment."],
      ["inquinare", "to pollute", "🏭", "verb", "", "Le macchine inquinano molto.", "Cars pollute a lot."],
      ["il paesaggio", "landscape", "🖼️", "noun", "m", "Il paesaggio è impressionante.", "The landscape is stunning."],
      ["selvatico", "wild", "🐺", "adjective", "", "È una zona selvatica.", "It's a wild area."],
    ],
  },

  {
    theme: "nature", tier: "B1", rows: [
      ["la campagna profonda", "rural environment", "🚜", "phrase", "", "Sono cresciuto nella campagna profonda.", "I grew up in the countryside."],
      ["la catena montuosa", "mountain range", "🏔️", "noun", "f", "La catena montuosa separa i paesi.", "The mountain range separates the countries."],
      ["il ruscello", "stream", "🏞️", "noun", "m", "Il ruscello scende dalla montagna.", "The stream comes down from the mountain."],
      ["la fauna", "wildlife", "🦌", "noun", "f", "La fauna locale è variata.", "The local wildlife is varied."],
      ["la flora", "flora", "🌿", "noun", "f", "La flora del bosco è fitta.", "The forest flora is dense."],
      ["seminare", "to sow", "🌱", "verb", "", "Seminiamo il grano in primavera.", "We sow wheat in spring."],
      ["la coltivazione", "crop", "🌾", "noun", "f", "La coltivazione ha bisogno d'acqua.", "The crop needs water."],
      ["la vetta", "summit", "🗻", "noun", "f", "Raggiungiamo la vetta a mezzogiorno.", "We reach the summit at midday."],
      ["proteggere", "to protect", "🛡️", "verb", "", "Dobbiamo proteggere la natura.", "We must protect nature."],
      ["la specie", "species", "🐾", "noun", "f", "È una specie in pericolo.", "It's an endangered species."],
    ],
  },

  // ---- Describing Things -----------------------------------------------
  {
    theme: "describing", tier: "A1", rows: [
      ["grande", "big", "🐘", "adjective", "", "È una casa grande.", "It's a big house."],
      ["piccolo", "small", "🐁", "adjective", "", "Ho una macchina piccola.", "I have a small car."],
      ["nuovo", "new", "✨", "adjective", "", "È il mio telefono nuovo.", "It's my new phone."],
      ["vecchio", "old", "📦", "adjective", "", "È un edificio vecchio.", "It's an old building."],
      ["buono", "good", "👍", "adjective", "", "È un buon libro.", "It's a good book."],
      ["cattivo", "bad", "👎", "adjective", "", "Fa brutto tempo.", "The weather is bad."],
      ["bello", "pretty", "🌷", "adjective", "", "Che bel vestito.", "What a pretty dress."],
      ["brutto", "ugly", "🙈", "adjective", "", "Il quadro è un po' brutto.", "The painting is a bit ugly."],
      ["lungo", "long", "📏", "adjective", "", "È una strada lunga.", "It's a long road."],
      ["corto", "short", "✂️", "adjective", "", "Il video è corto.", "The video is short."],
    ],
  },
  {
    theme: "describing", tier: "A2", rows: [
      ["gentile", "kind", "🤗", "adjective", "", "Il cameriere è molto gentile.", "The waiter is very kind."],
      ["intelligente", "intelligent", "🧠", "adjective", "", "È una bambina intelligente.", "She's an intelligent girl."],
      ["forte", "strong", "💪", "adjective", "", "Il caffè è molto forte.", "The coffee is very strong."],
      ["debole", "weak", "🥀", "adjective", "", "Il segnale è debole.", "The signal is weak."],
      ["veloce", "fast", "⚡", "adjective", "", "È un treno veloce.", "It's a fast train."],
      ["lento", "slow", "🐢", "adjective", "", "Il servizio è molto lento.", "The service is very slow."],
      ["pulito", "clean", "🧼", "adjective", "", "La camera è pulita.", "The room is clean."],
      ["sporco", "dirty", "🧽", "adjective", "", "Il pavimento è sporco.", "The floor is dirty."],
      ["difficile", "difficult", "🧗", "adjective", "", "L'esercizio è difficile.", "The exercise is difficult."],
      ["facile", "easy", "✅", "adjective", "", "La domanda è facile.", "The question is easy."],
    ],
  },
  {
    theme: "describing", tier: "B1", rows: [
      ["stretto", "narrow", "↔️", "adjective", "", "La strada è molto stretta.", "The street is very narrow."],
      ["largo", "wide", "📐", "adjective", "", "Il fiume è molto largo qui.", "The river is very wide here."],
      ["profondo", "deep", "🌊", "adjective", "", "Il lago è molto profondo.", "The lake is very deep."],
      ["leggero", "light", "🪶", "adjective", "", "La valigia è leggera.", "The suitcase is light."],
      ["pesante", "heavy", "🏋️", "adjective", "", "Questa scatola è molto pesante.", "This box is very heavy."],
      ["morbido", "soft", "🧴", "adjective", "", "Il tessuto è molto morbido.", "The fabric is very soft."],
      ["ruvido", "rough", "🪵", "adjective", "", "La superficie è ruvida.", "The surface is rough."],
      ["lucido", "shiny", "✨", "adjective", "", "Il metallo è lucido.", "The metal is shiny."],
      ["scuro", "dark", "🌑", "adjective", "", "La camera è scura.", "The room is dark."],
      ["trasparente", "transparent", "🔍", "adjective", "", "Il vetro è trasparente.", "The glass is transparent."],
    ],
  },

  // ---- B2 tier: more abstract, nuanced vocabulary for the upper-intermediate
  // path. Authored to stretch beyond concrete A1–B1 nouns into feelings, work,
  // society, and shades of description. -----------------------------------
  {
    theme: "feelings", tier: "B2", rows: [
      ["lo stress", "overwhelm", "😮‍💨", "noun", "m", "Sento uno stress enorme con tanto lavoro.", "I feel huge overwhelm with so much work."],
      ["l'incertezza", "uncertainty", "🌫️", "noun", "f", "Viviamo un'epoca di incertezza.", "We live in a time of uncertainty."],
      ["il risentimento", "resentment", "💢", "noun", "m", "Prova risentimento dopo il litigio.", "He holds resentment since the argument."],
      ["la serenità", "serenity", "🧘", "noun", "f", "Cerco un po' di serenità.", "I seek a little serenity."],
      ["sopraffatto", "overwhelmed", "🥵", "adjective", "", "Era sopraffatto dalle notizie.", "He was overwhelmed by the news."],
      ["rassegnarsi", "to resign oneself", "🤷", "verb", "", "Si è rassegnata a perdere il treno.", "She resigned herself to missing the train."],
      ["caloroso", "endearing", "🤗", "adjective", "", "Fu un incontro caloroso.", "It was a heartwarming encounter."],
      ["lo struggimento", "longing", "🕰️", "noun", "m", "Prova struggimento per il suo paese.", "She feels longing for her hometown."],
      ["travolto", "swamped", "🌊", "adjective", "", "Mi sento travolta questa settimana.", "I feel swamped this week."],
      ["commovente", "moving", "🥹", "adjective", "", "Il discorso è stato commovente.", "The speech was moving."],
    ],
  },
  {
    theme: "work-study", tier: "B2", rows: [
      ["l'incarico", "remit / task", "🎯", "noun", "m", "Ha svolto il suo incarico con efficacia.", "He carried out his remit efficiently."],
      ["la sfida", "challenge", "🧗", "noun", "f", "Ha accettato la sfida senza esitare.", "He took on the challenge without hesitating."],
      ["l'assunzione", "hiring / recruitment", "📝", "noun", "f", "Le assunzioni sono state congelate quest'anno.", "Hiring was frozen this year."],
      ["la prestazione", "performance", "📈", "noun", "f", "Valutano la prestazione ogni trimestre.", "They assess performance each quarter."],
      ["delegare", "to delegate", "🤝", "verb", "", "Ho imparato a delegare i compiti.", "I learned to delegate tasks."],
      ["il sindacato", "trade union", "✊", "noun", "m", "Il sindacato ha indetto una riunione.", "The union called a meeting."],
      ["il posto vacante", "job vacancy", "🪑", "noun", "m", "C'è un posto vacante nel team.", "There is a vacancy in the team."],
      ["la leadership", "leadership", "🧭", "noun", "f", "Ha dimostrato una grande leadership.", "She showed great leadership."],
      ["polivalente", "versatile", "🧰", "adjective", "", "Cercano un profilo polivalente.", "They want a versatile profile."],
      ["la redditività", "profitability", "📈", "noun", "f", "Analizzano la redditività del progetto.", "They analyse the project's profitability."],
    ],
  },
  {
    theme: "town", tier: "B2", rows: [
      ["il consigliere comunale", "town councillor", "🧑‍⚖️", "noun", "m", "Il consigliere comunale ha presentato il progetto.", "The councillor presented the project."],
      ["l'ordinanza", "bylaw", "📋", "noun", "f", "L'ordinanza vieta il rumore notturno.", "The bylaw bans night-time noise."],
      ["la metropoli", "metropolis", "🌃", "noun", "f", "La metropoli non dorme mai.", "The metropolis never sleeps."],
      ["l'hinterland", "city outskirts", "🏙️", "noun", "m", "Vivono nell'hinterland.", "They live on the city outskirts."],
      ["l'immobile", "property", "🏢", "noun", "m", "L'immobile è in vendita.", "The building is for sale."],
      ["trafficato", "busy (with traffic)", "🚦", "adjective", "", "È un viale molto trafficato.", "It's a very busy avenue."],
      ["il cantiere", "roadworks / building site", "🚧", "noun", "m", "C'è un cantiere lungo tutto il viale.", "There are roadworks along the whole avenue."],
      ["il vicinato", "neighbourhood", "🏡", "noun", "m", "Il vicinato è molto tranquillo.", "The neighbourhood is very quiet."],
      ["le fogne", "sewer system", "🚰", "noun", "f", "Rinnovano le fogne del quartiere.", "They are renovating the neighbourhood's sewer system."],
      ["la pratica", "paperwork / procedure", "📋", "noun", "f", "Ho fatto la pratica online.", "I did the procedure online."],
    ],
  },
  {
    theme: "nature", tier: "B2", rows: [
      ["il pendio", "hillside", "⛰️", "noun", "m", "Le case sorgono sul pendio.", "The houses rise on the hillside."],
      ["il surriscaldamento", "warming", "🌡️", "noun", "m", "Il surriscaldamento globale avanza.", "Global warming is advancing."],
      ["l'inondazione", "flood", "🌊", "noun", "f", "L'inondazione ha devastato i campi.", "The flood devastated the fields."],
      ["lo sviluppo sostenibile", "sustainable development", "♻️", "phrase", "", "Puntano sullo sviluppo sostenibile.", "They back sustainable development."],
      ["la biodiversità", "biodiversity", "🦋", "noun", "f", "La biodiversità è in pericolo.", "Biodiversity is under threat."],
      ["abbattere", "to fell (trees)", "🪓", "verb", "", "Hanno abbattuto mezzo ettaro di bosco.", "They felled half a hectare of forest."],
      ["lo sversamento", "spill", "🛢️", "noun", "m", "Lo sversamento ha danneggiato la costa.", "The spill damaged the coast."],
      ["rinnovabile", "renewable", "🔋", "adjective", "", "Usiamo energia rinnovabile.", "We use renewable energy."],
      ["il rifiuto", "waste", "🗑️", "noun", "m", "Ricicliamo tutti i rifiuti.", "We recycle all the waste."],
      ["preservare", "to preserve", "🌲", "verb", "", "Bisogna preservare i boschi.", "We must preserve the forests."],
    ],
  },
  {
    theme: "describing", tier: "B2", rows: [
      ["indispensabile", "essential", "❗", "adjective", "", "Il tuo aiuto è indispensabile.", "Your help is essential."],
      ["appariscente", "striking", "🌟", "adjective", "", "Indossa un cappotto appariscente.", "She wears a striking coat."],
      ["insolito", "unusual", "❕", "adjective", "", "È stata una reazione insolita.", "It was an unusual reaction."],
      ["affidabile", "reliable", "✅", "adjective", "", "È una fonte affidabile.", "It's a reliable source."],
      ["scarso", "scarce", "📉", "adjective", "", "L'acqua è scarsa lì.", "Water is scarce there."],
      ["sorprendente", "surprising", "😲", "adjective", "", "Il risultato è stato sorprendente.", "The result was surprising."],
      ["duraturo", "long-lasting", "⏱️", "adjective", "", "Cerchiamo una soluzione duratura.", "We want a long-lasting solution."],
      ["ambiguo", "ambiguous", "❓", "adjective", "", "La sua risposta è stata ambigua.", "His answer was ambiguous."],
      ["scioccante", "shocking", "💥", "adjective", "", "Le immagini sono scioccanti.", "The images are shocking."],
      ["articolato", "nuanced", "🎚️", "adjective", "", "Ha dato un'opinione articolata.", "She gave a nuanced opinion."],
    ],
  },
  // ---- Science & Technology (B2) --------------------------------------
  {
    theme: "science-tech", tier: "B2", rows: [
      ["la ricerca", "research", "🔬", "noun", "f", "La ricerca è durata tre anni.", "The research lasted three years."],
      ["il progresso", "breakthrough / advance", "🚀", "noun", "m", "È un progresso scientifico fondamentale.", "It is a key scientific advance."],
      ["il dato", "piece of data", "📊", "noun", "m", "I dati confermano la teoria.", "The data support the theory."],
      ["la rete", "network", "🕸️", "noun", "f", "La rete è andata giù stamattina.", "The network went down this morning."],
      ["il dispositivo", "device", "📱", "noun", "m", "Sincronizza tutti i tuoi dispositivi.", "Sync all your devices."],
      ["aggiornare", "to update", "🔄", "verb", "", "Devi aggiornare l'applicazione.", "You have to update the app."],
      ["la sperimentazione", "trial / test", "🧪", "noun", "f", "La sperimentazione clinica è stata un successo.", "The clinical trial was successful."],
      ["l'impronta", "footprint / trace", "👣", "noun", "f", "Riduciamo l'impronta di carbonio.", "We are cutting the carbon footprint."],
      ["wireless", "wireless", "📶", "adjective", "", "Uso degli auricolari wireless.", "I use wireless headphones."],
      ["la scoperta", "finding", "💡", "noun", "f", "La scoperta ha sorpreso tutti.", "The finding surprised everyone."],
    ],
  },
  // ---- Politics & Society (B2) ----------------------------------------
  {
    theme: "politics", tier: "B2", rows: [
      ["il governo", "government", "🏛️", "noun", "m", "Il governo ha annunciato delle riforme.", "The government announced reforms."],
      ["la legge", "law", "⚖️", "noun", "f", "La nuova legge entra in vigore domani.", "The new law takes effect tomorrow."],
      ["il diritto", "right", "✊", "noun", "m", "È un diritto fondamentale.", "It is a fundamental right."],
      ["la cittadinanza", "citizenry", "🧑‍🤝‍🧑", "noun", "f", "La cittadinanza chiede trasparenza.", "Citizens demand transparency."],
      ["votare", "to vote", "🗳️", "verb", "", "Domani andiamo a votare.", "Tomorrow we are going to vote."],
      ["il partito", "political party", "🚩", "noun", "m", "Il partito ha perso le elezioni.", "The party lost the election."],
      ["la disuguaglianza", "inequality", "⚖️", "noun", "f", "C'è molta disuguaglianza economica.", "There is a lot of economic inequality."],
      ["l'accordo", "agreement", "🤝", "noun", "m", "Hanno raggiunto un accordo storico.", "They reached a historic agreement."],
      ["controverso", "controversial", "🔥", "adjective", "", "È stata una decisione controversa.", "It was a controversial decision."],
      ["la manifestazione", "protest / demonstration", "📢", "noun", "f", "Migliaia di persone hanno partecipato alla manifestazione.", "Thousands attended the protest."],
    ],
  },
  // ---- Media & News (B2) ----------------------------------------------
  {
    theme: "media", tier: "B2", rows: [
      ["la notizia", "news item", "📰", "noun", "f", "La notizia si è diffusa in fretta.", "The news spread quickly."],
      ["il titolo", "headline", "🗞️", "noun", "m", "Il titolo era esagerato.", "The headline was exaggerated."],
      ["la fonte", "source", "🔎", "noun", "f", "Bisogna citare la fonte.", "You have to cite the source."],
      ["la stampa", "the press", "📑", "noun", "f", "La stampa ha seguito l'evento.", "The press covered the event."],
      ["il reportage", "report / feature", "🎥", "noun", "m", "Ho visto un reportage sull'argomento.", "I saw a feature on the topic."],
      ["diffondere", "to spread / broadcast", "📡", "verb", "", "Hanno diffuso il video sui social.", "They spread the video on social media."],
      ["tendenzioso", "biased", "↔️", "adjective", "", "L'articolo è molto tendenzioso.", "The article is very biased."],
      ["la conferenza stampa", "press conference", "🎙️", "phrase", "", "Hanno indetto una conferenza stampa.", "They called a press conference."],
      ["la bufala", "hoax / fake news", "🚫", "noun", "f", "Si è rivelata una bufala.", "It turned out to be a hoax."],
      ["veritiero", "truthful", "✅", "adjective", "", "Cerca informazioni veritiere.", "Look for truthful information."],
    ],
  },
];

function buildVocabulary() {
  const out = [];
  let rank = 1;
  for (const block of [...THEMES, ...EXTRA_THEME_BLOCKS]) {
    for (const [spanish, english, emoji, pos, gender, exEs, exEn] of block.rows) {
      out.push({
        spanish,
        english,
        emoji,
        theme: block.theme,
        tier: block.tier,
        part_of_speech: pos,
        gender: gender || "none",
        example_es: exEs,
        example_en: exEn,
        rank: rank++,
      });
    }
  }
  return out;
}

// Theme registry — drives the browse view's filter chips and section headers.
export const VOCAB_THEMES = [
  { id: "identity", label: "Identity", emoji: "🪪" },
  { id: "family", label: "Family", emoji: "👪" },
  { id: "house", label: "House & Home", emoji: "🏠" },
  { id: "food", label: "Food & Drink", emoji: "🍽️" },
  { id: "body-health", label: "Body & Health", emoji: "🩺" },
  { id: "daily-routine", label: "Daily Routine", emoji: "⏰" },
  { id: "work-study", label: "Work & Study", emoji: "💼" },
  { id: "travel", label: "Travel", emoji: "🧳" },
  { id: "time-weather", label: "Time & Weather", emoji: "🌤️" },
  { id: "shopping", label: "Shopping", emoji: "🛒" },
  { id: "town", label: "Town & Directions", emoji: "🏙️" },
  { id: "leisure", label: "Leisure", emoji: "🎉" },
  { id: "feelings", label: "Feelings", emoji: "😊" },
  { id: "nature", label: "Nature", emoji: "🌳" },
  { id: "describing", label: "Describing Things", emoji: "🎨" },
  { id: "science-tech", label: "Science & Technology", emoji: "🔬" },
  { id: "politics", label: "Politics & Society", emoji: "🏛️" },
  { id: "media", label: "Media & News", emoji: "📰" },
];

export const VOCAB_TIERS = ["A1", "A2", "B1", "B2", "C1", "C2"];

// Flattened, ranked card list — the single source for the view and the seed.
export const VOCABULARY_CARDS = buildVocabulary();
